import { Permission } from 'iam/dtos/permissions'
import Member from 'iam/models/Member'
import Team from 'iam/models/Team'
import Workspace from 'iam/models/Workspace'
import { PermissionSubject } from 'iam/PermissionSubject'
import { GetCurrentMember } from 'iam/SessionStore'
import { GetTeamMember, GetTeamMembers } from 'iam/TeamMemberStore'
import { PrimeTeam } from 'iam/TeamStore'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import { collection } from 'infra/collection'
import { Model } from 'infra/model'
import {
  CollaboratorRole,
  CollaboratorType,
  JobBillingType,
  JobDTO,
  JobMemberDTO,
  JobStatus,
  JobType,
  JobUserDTO,
  PayoutSplitDTO,
  TaskDesignee,
} from 'jobs/dtos/job'
import { AnswerDTO } from 'jobs/dtos/questions'
import { LegendVersionId, PublishedLegend } from 'legends/PublishedLegend'
import { PrimePublishedLegend } from 'legends/PublishedLegendStore'
import { isNil, once } from 'lodash'
import { PrimeConversation } from 'messaging/ConversationStore'
import Conversation from 'messaging/models/Conversation'
import { action, computed, makeObservable, observable } from 'mobx'
import { distinctUntilChanged } from 'rxjs/operators'
import { Id } from 'types/definitions'
import { DateZero, maybeParseDate } from 'utils/date-util'
import { toStream } from 'utils/mobx-util'
import { roundToPrecision } from 'utils/number-util'
import { isNotNil } from 'utils/object-util'
import { match } from 'utils/pattern-match-util'
import { shareLatest } from 'utils/rx-util'
import User from '../iam/models/User'
import { PrimeUser } from '../iam/UserStore'
import Consultation, { ConsultationStatus } from './consultations/Consultation'
import { PrimeMemberByPublicId } from 'iam/MemberStore'
import { Room } from 'messaging/rooms/Room'
import { PrimeRoom } from 'messaging/rooms/RoomStore'

export enum JobLegendServiceType {
  TAX = 'TAX',
  BOOKKEEPING = 'BOOKKEEPING',
  CONSULTATION = 'CONSULTATION',
  MISC = 'MISC',
  UNSPECIFIED = 'UNSPECIFIED',
}

export enum Resolution {
  /**
   * Indicates that the job was completed.
   */
  Done,

  /**
   * Indicates that the job was cancelled.
   */
  Cancelled,

  /**
   * Indicates that the job was created for test purposes.
   */
  Test,
}

/**
 * Default rating configuration.
 */
const RatingConfig = {
  default: {
    platformRatingEnabled: false,
    proRatingEnabled: false,
    customerRatingMethod: 'stars',
  } as RatingConfig,
}

export default class Job extends Model<JobDTO, Job> {
  /**
   * Don't use outside this class. We want to keep Legend dependency to a minimum.
   */
  private legend: any = {}

  /**
   * Whether the job is a draft.
   */
  @computed
  get isDraft() {
    return this.status === 'UNDER_CONSTRUCTION'
  }

  /**
   * The subject to check permissions on.
   */
  readonly permissionSubject: PermissionSubject =
    new AggregatingPermissionSubject(
      this,
      this.getCurrentMember,
      this.getTeamMember,
    )

  @observable private allowManualPaymentCollection = false
  @observable private billingEnabled = false
  @observable private _total = 0
  @observable private _paidAmount = 0
  @observable primaryAmount = 0
  @observable providerCutAmount = 0
  @observable stripeCharges: string[] = []
  @observable stripeRefunds: string[] = []
  @observable amendments: Amendment[] = []
  @observable resolution?: Resolution | null = null
  @observable deferPaymentInfoCollection = false
  @observable dateDeadline: Date | null = null
  @observable dateCreated = DateZero()
  @observable dateModified = DateZero()
  @observable dateSubmitted: Date | null = null
  @observable dateAccepted: Date | null = null
  @observable dateClosed: Date | null = null
  @observable dateLastActivity = DateZero()
  @observable id = Id.none
  @observable jobScopeItems: JobScopeItem[] = []
  @observable legendId = ''
  @observable legendName = ''
  @observable legendVersionId = LegendVersionId.none
  @observable legendServiceType: JobLegendServiceType =
    JobLegendServiceType.UNSPECIFIED

  @observable name = ''
  @observable description = ''
  @observable shortId = ''
  @observable couponCodes = ''
  @observable couponAmount = 0
  @observable status: JobStatus = 'UNDER_CONSTRUCTION'
  @observable workspace = Workspace.none
  @observable milestones: Milestone[] = []
  @observable conversation: Conversation | null = null
  @observable allowScheduling = false
  @observable allowProSelection = true
  @observable alwaysEnableVoiceCall = false
  @observable ownerTeam: Team | null = null
  @observable teams: Team[] = []
  @observable infoLines: InfoLine[] = []
  @observable requestedProId: Id | null = Id.none
  @observable requestedProPublicId: Id | null = Id.none
  @observable requestedPro: Member | null = null
  @observable paymentInfoStatus = PaymentInfoStatus.NotProvided
  @observable.shallow answers: AnswerDTO[] = []
  @observable.shallow ratingConfig: RatingConfig = RatingConfig.default
  @observable waitingOn: WaitingOn | null = null
  @observable payoutSplit: PayoutSplit | null = null
  @observable currentUserAllocation: number | null = null
  @observable billingType: JobBillingType = 'STRIPE'
  @observable consultation?: Consultation
  @observable jobType?: JobType

  /**
   * Status changes observable stream.
   */
  statusChange$ = toStream(() => this.status, true).pipe(
    distinctUntilChanged(),
    shareLatest(),
  )

  /**
   * Coupon changes observable stream.
   */
  couponChange$ = toStream(() => this.couponCodes, true).pipe(
    distinctUntilChanged(),
    shareLatest(),
  )

  /**
   * Collaborators collection.
   */
  collaborators = collection<JobMemberDTO, Collaborator>({
    getModelId: (m) => m.member.user.publicId,
    getDataId: (m) => m.user && m.user.user_public_id,
    create: () =>
      new Collaborator(this, this.primeMemberByPublicId, this.primeTeam),
  })

  constructor(
    private primeWorkspace: PrimeWorkspace,
    private primeUser: PrimeUser,
    private primeConversation: PrimeConversation,
    private primeTeam: PrimeTeam,
    private primePublishedLegend: PrimePublishedLegend,
    private getTeamMember: GetTeamMember,
    private getTeamMembers: GetTeamMembers,
    private getCurrentMember: GetCurrentMember,
    private primeMemberByPublicId: PrimeMemberByPublicId,
    private primeRoom: PrimeRoom,
  ) {
    super()
    makeObservable(this)
  }

  static get none() {
    return lazyNone()
  }

  @computed
  get jobScopeItemsNames() {
    return this.jobScopeItems.map((item) => item.name)
  }

  @computed
  get jobBundle() {
    return this.jobScopeItems.find((item) => item.bundle_id)
  }

  @computed
  get total() {
    return this._total
  }

  @computed
  get paidAmount() {
    return this._paidAmount
  }

  @computed
  get hasActiveConsultation() {
    return isNotNil(this.consultation)
      ? this.consultation.status !== ConsultationStatus.Cancelled &&
          this.consultation.status !== ConsultationStatus.Done
      : false
  }

  @computed
  get balance() {
    return this.total - this.paidAmount
  }

  /**
   * Display name.
   */
  @computed
  get displayName() {
    return this.name || this.legendName
  }

  /**
   * Whether we can add clients as a team member of the client team
   */
  @computed
  get canAddClientsAsTeamMember() {
    const currentTeamMemberOfOwnerTeam = this.currentTeamMemberOfOwnerTeam
    return Boolean(
      currentTeamMemberOfOwnerTeam?.hasAllPermissions('UPDATE_TEAM_JOBS'),
    )
  }

  /**
   * Whether we can remove clients as a team member
   */
  @computed
  get canRemoveClientsAsTeamMember() {
    if (this.supportingClients.length === 0) {
      return false
    }

    const currentTeamMemberOfOwnerTeam = this.currentTeamMemberOfOwnerTeam
    return Boolean(
      currentTeamMemberOfOwnerTeam?.hasAllPermissions('UPDATE_TEAM_JOBS'),
    )
  }

  /**
   * Whether we can remove clients as a collaborator on the job
   */
  @computed
  get canRemoveClientsAsCollaborator() {
    if (this.supportingClients.length === 0) {
      return false
    }

    return Boolean(this.championClient?.member.isCurrentUser)
  }

  /**
   * Whether the current user is allowed to view job variables.
   */
  @computed
  get canViewQuestionVariables() {
    return this.getCurrentMember(this.workspace.id).hasSomePermissions(
      'ADMIN_JOB_VIEW',
    )
  }

  /**
   * Whether the current user is allowed to view the Legend name.
   */
  @computed
  get canViewLegendNameAsAdmin() {
    return this.getCurrentMember(this.workspace.id).hasPermission(
      'ADMIN_LEGEND_VIEW',
    )
  }

  /**
   * Whether billing is enabled for the job.
   */
  @computed
  get isBillingEnabled() {
    return this.workspace.features.billing.enabled && this.billingEnabled
  }

  /**
   * The balance status.
   */
  @computed
  get balanceStatus(): BalanceStatus {
    // NOTE: Ideally the API would return this however
    // adding a transform that does that everywhere is a bitch in Work API
    // so doing it here for the time being. And ideally, not all payment info
    // is "leaked" to every user. ¯\_(ツ)_/¯
    if (this._paidAmount > this._total) {
      return BalanceStatus.BalanceNegative
    }

    if (this._paidAmount < this._total) {
      return BalanceStatus.BalancePositive
    }

    return BalanceStatus.BalanceZero
  }

  /**
   * Current milestone.
   */
  @computed
  get currentMilestone(): Milestone | null {
    if (this.status === 'UNDER_CONSTRUCTION') {
      return null
    }

    return this.milestones.find((m) => !m.completed) || null
  }

  /**
   * Whether the current user is a Provider-role collaborator.
   */
  @computed
  get isProviderCollaborator() {
    return this.collaborators.some(
      (c) => c.type === 'PROVIDER' && c.member.isCurrentUser,
    )
  }

  /**
   * Whether the current user is a champion Client-role collaborator.
   */
  @computed
  get isChampionClientCollaborator() {
    return this.collaborators.some(
      (c) =>
        c.type === 'CLIENT' && c.role === 'CHAMPION' && c.member.isCurrentUser,
    )
  }

  /**
   * Whether the current user is a supporting Client-role collaborator.
   */
  @computed
  get isSupportingClientCollaborator() {
    return this.collaborators.some(
      (c) =>
        c.type === 'CLIENT' &&
        c.role === 'SUPPORTING' &&
        c.member.isCurrentUser,
    )
  }

  /**
   * Whether job scope change requests are enabled in the workspace.
   */
  @computed
  get isJobScopeChangeRequestsEnabled() {
    return (
      this.workspace.config.combined_pricing
        ?.job_scope_change_requests_enabled ?? false
    )
  }

  /**
   * Determines whether a job scope update can be made.
   */
  @computed
  get canUpdateJobScope() {
    return (
      this.isJobScopeChangeRequestsEnabled &&
      !this.completed &&
      !this.isDraft &&
      this.getCurrentMember(this.workspace.id).hasSomePermissions(
        'ADMIN_JOB_SCOPE_CHANGE_MANAGE',
        'ADMIN_JOB_SCOPE_CHANGE_RESOLVE',
      )
    )
  }

  /**
   * Determines whether job scope change requests can be shown.
   */
  @computed
  get canViewScopeChangeRequests() {
    if (!this.isJobScopeChangeRequestsEnabled || this.isDraft) {
      return false
    }

    if (this.currentUserCollaborator) {
      return true
    }

    const isAdmin = this.getCurrentMember(this.workspace.id).hasSomePermissions(
      'ADMIN_JOB_SCOPE_CHANGE_MANAGE',
    )

    if (isAdmin) {
      return true
    }

    return this.teamIsCollaborator
  }

  /**
   * Determines whether job scope changes can be made.
   */
  @computed
  get canRequestScopeChanges() {
    return (
      this.isJobScopeChangeRequestsEnabled &&
      ['CLAIMED', 'ON_HOLD', 'IDLE'].includes(this.status) &&
      this.getCurrentMember(this.workspace.id).hasSomePermissions(
        'JOB_SCOPE_CHANGE_CREATE',
      )
    )
  }

  /**
   * Determines whether we can manage supporting pros.
   */
  @computed
  get canManageCollaboratorsAsAdmin() {
    return (
      !this.completed &&
      this.getCurrentMember(this.workspace.id).hasSomePermissions(
        'ADMIN_JOB_SUPPORTING_PROVIDER_MANAGE',
      )
    )
  }

  /**
   * Collaborators that are not the current user.
   */
  @computed
  get otherCollaborators() {
    return this.collaborators.filter((c) => !c.member.isCurrentUser)
  }

  /**
   * Supporting Pros on the Job.
   */
  @computed
  get supportingPros() {
    return this.collaborators.filter(
      (c) => c.role === 'SUPPORTING' && c.type === 'PROVIDER',
    )
  }

  /**
   * Supporting Clients on the Job.
   */
  @computed
  get supportingClients() {
    return this.collaborators.filter(
      (c) => c.role === 'SUPPORTING' && c.type === 'CLIENT',
    )
  }

  /**
   * Gets the current user's workspace id by using the job.
   */
  @computed
  get currentMemberWorkspaceId() {
    const currentMember = this.getCurrentMember(this.workspace.id)
    return Id.assert(currentMember.workspace.id)
  }

  /**
   * Collaborator entry for the current user.
   */
  @computed
  get currentUserCollaborator() {
    return this.collaborators.find((c) => c.member.isCurrentUser) || null
  }

  /**
   * The Client Champion of the job.
   */
  @computed
  get owner() {
    const c = this.collaborators.find(
      (c) => c.type === 'CLIENT' && c.role === 'CHAMPION',
    )
    if (!c) {
      return Member.none
    }
    return c.member
  }

  /**
   * Determine whether your team is a part of this job
   * and whether you have the right to see it.
   */
  @computed
  get teamIsCollaborator() {
    const currentMember = this.getCurrentMember()
    return this.teams.some((team) => {
      const teamMember = this.getTeamMember(
        team.id,
        currentMember.user.publicId,
      )
      return teamMember?.hasPermission('VIEW_TEAM_JOBS')
    })
  }

  /**
   * Current client team member of owner team.
   */
  @computed
  get currentTeamMemberOfOwnerTeam() {
    if (!this.ownerTeam) {
      return
    }

    return this.getTeamMember(
      this.ownerTeam.id,
      this.getCurrentMember(this.workspace.id).user.publicId,
    )
  }

  @computed
  get clientTeamMembers() {
    if (!this.ownerTeam) {
      return []
    }

    return this.getTeamMembers(this.ownerTeam.id)
  }

  /**
   * If the current user is a part of the owner team.
   */
  @computed
  get isPartOfClientTeam() {
    return Boolean(this.currentTeamMemberOfOwnerTeam)
  }

  /**
   * Whether the current user has CHAT_ADD permission on the owner team.
   * This allows owner team members to send messages even if they are not
   * explicit conversation participants.
   */
  @computed
  get hasOwnerTeamChatPermission() {
    return Boolean(this.currentTeamMemberOfOwnerTeam?.hasPermission('CHAT_ADD'))
  }

  /**
   * If the job is a client team job.
   */
  @computed
  get isClientTeamJob() {
    return Boolean(this.ownerTeam)
  }

  /**
   * If the current user is a part of the pro team.
   */
  @computed
  get isPartOfProTeam() {
    const proTeam = this.collaborators.find(
      (c) => c.type === 'PROVIDER' && c.team !== null,
    )?.team

    return Boolean(
      proTeam?.id &&
        this.getTeamMember(
          proTeam?.id,
          this.getCurrentMember(this.workspace.id).user.publicId,
        ),
    )
  }

  /**
   * Whether the current member is allowed to view the price.
   */
  @computed
  get canViewPrice() {
    return (
      this.permissionSubject.hasSomePermissions(['VIEW_PRICING']) ||
      this.getCurrentMember(this.workspace.id).hasSomePermissions(
        'ADMIN_JOB_VIEW',
      ) ||
      this.getCurrentMember(this.workspace.id).hasSomePermissions(
        'ADMIN_VIEW_PRICING',
      )
    )
  }

  /**
   * Whether the current user is allowed to view the Captured information card.
   */
  @computed
  get canViewCapturedInfoCardAsAdmin() {
    return this.getCurrentMember(this.workspace.id).hasPermission(
      'ADMIN_LEGEND_VIEW',
    )
  }

  /**
   * Whether we can settle the balance.
   */
  @computed
  get canSettleBalance() {
    return (
      this.allowManualPaymentCollection &&
      this.currentUserCollaborator?.role === 'CHAMPION' &&
      this.currentUserCollaborator?.type === 'PROVIDER' &&
      this.balanceStatus !== BalanceStatus.BalanceZero
    )
  }

  /**
   * Whether the current user is allowed to submit the request.
   */
  @computed
  get canSubmit() {
    const currentMember = this.getCurrentMember(this.workspace.id)
    // If CU is an admin, they can submit.
    if (currentMember.hasSomePermissions('ADMIN_JOB_CREATE')) {
      return true
    }

    // If the job was created for a Team, check the CU's team member perm
    // for that team.
    if (this.ownerTeam) {
      const teamMemberForOwnerTeam = this.getTeamMember(
        this.ownerTeam.id,
        currentMember.user.publicId,
      )

      return teamMemberForOwnerTeam?.hasPermission('CREATE_TEAM_JOBS') ?? false
    }

    return Boolean(
      this.currentUserCollaborator?.member.hasSomePermissions('JOB_CREATE'),
    )
  }

  /**
   * Determines whether we can add a champion Pro on the job.
   */
  @computed
  get canAddChampionProviderAsAdmin() {
    return (
      !this.completed &&
      (this.status === 'UNCLAIMED' ||
        (this.status === 'INFO_GATHERING' &&
          this.consultation !== undefined)) &&
      this.getCurrentMember(this.workspace.id).hasAllPermissions(
        'ADMIN_JOB_CHAMPION_PROVIDER_MANAGE',
        'ADMIN_USER_LIST',
        'ADMIN_USER_READ',
        'ADMIN_JOB_VIEW',
      )
    )
  }

  /**
   * Determines whether we can manage coupons for a job.
   */
  @computed
  get canManageCouponAsAdmin() {
    return (
      !this.completed &&
      this.workspace.features.billing.couponsEnabled &&
      this.getCurrentMember(this.workspace.id).hasAllPermissions(
        'ADMIN_BILLING_MANAGE',
      )
    )
  }

  /**
   * Determines whether we can request a champion Pro on the job.
   */
  @computed
  get canRequestChampionProviderAsAdmin() {
    return (
      this.status === 'INFO_GATHERING' &&
      this.getCurrentMember(this.workspace.id).hasAllPermissions(
        'ADMIN_JOB_CHAMPION_PROVIDER_MANAGE',
      )
    )
  }

  /**
   * Determines whether we can add a supporting Pro on the job.
   */
  @computed
  get canAddSupportingProviderAsAdmin() {
    return (
      !this.completed &&
      (this.status === 'CLAIMED' ||
        this.status === 'ON_HOLD' ||
        this.status === 'IDLE') &&
      this.getCurrentMember(this.workspace.id).hasAllPermissions(
        'ADMIN_JOB_CHAMPION_PROVIDER_MANAGE',
        'ADMIN_JOB_SUPPORTING_PROVIDER_MANAGE',
        'ADMIN_USER_LIST',
        'ADMIN_USER_READ',
        'ADMIN_JOB_VIEW',
      )
    )
  }

  /**
   * Determines whether we can add a supporting Client on the job as an admin.
   */
  @computed
  get canAddSupportingClientAsAdmin() {
    return (
      !this.completed &&
      this.getCurrentMember(this.workspace.id).hasAllPermissions(
        'ADMIN_JOB_CLIENTS_MANAGE',
      ) &&
      this.getCurrentMember(this.workspace.id).hasSomePermissions(
        'ADMIN_USER_LIST',
        'ADMIN_CLIENT_USER_LIST',
      )
    )
  }

  /**
   * Determines whether we can remove a supporting Client on the job as an admin.
   */
  @computed
  get canRemoveSupportingClientsAsAdmin() {
    return (
      !this.completed &&
      this.getCurrentMember(this.workspace.id).hasAllPermissions(
        'ADMIN_JOB_CLIENTS_MANAGE',
      ) &&
      this.getCurrentMember(this.workspace.id).hasSomePermissions(
        'ADMIN_USER_LIST',
        'ADMIN_CLIENT_USER_LIST',
      )
    )
  }

  /**
   * Determines whether we can remove supporting Pros on the job as an admin.
   */
  @computed
  get canRemoveSupportingProsAsAdmin() {
    return (
      !this.completed &&
      this.getCurrentMember(this.workspace.id).hasAllPermissions(
        'ADMIN_JOB_SUPPORTING_PROVIDER_MANAGE',
        'ADMIN_USER_LIST',
      )
    )
  }

  /**
   * Determines whether we can rename the job.
   */
  @computed
  get canRename() {
    if (this.currentUserCollaborator?.type === 'PROVIDER') {
      return (
        this.status !== 'CLOSED' &&
        this.permissionSubject?.hasAllPermissions(['JOB_PROVIDER_UPDATE_NAME'])
      )
    }

    return (
      this.status !== 'CLOSED' &&
      (this.permissionSubject.hasAllPermissions(['JOB_UPDATE']) ||
        (this.currentUserCollaborator
          ? this.currentUserCollaborator.role === 'CHAMPION'
          : false))
    )
  }

  /**
   * Determines whether we can view amendments for the job.
   */
  @computed
  get canViewAmendments() {
    return Boolean(
      this.getCurrentMember(this.workspace.id).hasAllPermissions(
        'ADMIN_JOB_AMEND',
      ) && !this.isDraft,
    )
  }

  /**
   * Whether we are able to close the job as a collaborator.
   */
  @computed
  get canCloseAsCollaborator() {
    const currentUserType = this.currentUserCollaborator?.type
    const isClient = currentUserType === 'CLIENT'

    const allProviderTasksComplete = this.milestones.every((x) =>
      x.tasks.every((y) => y.designee !== 'Provider' || y.completed),
    )

    return (
      this.currentUserCollaborator &&
      !isClient &&
      this.permissionSubject.hasAllPermissions('JOB_CLOSE') &&
      this.status !== 'CLOSED' &&
      // Only provider tasks need to be finished - DEV-4387.
      allProviderTasksComplete
    )
  }

  /**
   * Whether we are able to close the job as an admin.
   */
  @computed
  get canCloseAsAdmin() {
    return (
      this.getCurrentMember(this.workspace.id).hasPermission(
        'ADMIN_JOB_CLOSE',
      ) &&
      !this.isDraft &&
      !this.completed
    )
  }

  /**
   * Whether we are able to transfer the job to pool as an admin.
   */
  @computed
  get canTransferToPoolAsAdmin() {
    return (
      !this.completed &&
      this.getCurrentMember(this.workspace.id).hasPermission(
        'ADMIN_JOB_TRANSFER',
      )
    )
  }

  /**
   * Whether we are able to remove the client team from the job as an admin.
   */
  @computed
  get canRemoveClientTeamAsAdmin() {
    if (!this.ownerTeam?.id) {
      return false
    }

    if (!this.teams.some((x) => x.id === this.ownerTeam?.id)) {
      return false
    }

    return this.getCurrentMember(this.workspace.id).hasAllPermissions(
      'ADMIN_MANAGE_TEAM_MEMBER',
      'ADMIN_ASSIGN_TEAM',
    )
  }

  /**
   * Completed status of the job.
   */
  @computed
  get completed() {
    return this.status === 'CLOSED'
  }

  /**
   * The Champion Provider of the job.
   */
  @computed
  get championProvider() {
    const provider = this.collaborators.find(
      (c) => c.role === 'CHAMPION' && c.type === 'PROVIDER',
    )

    if (!provider) {
      return null
    }

    return provider
  }

  @computed
  get championClient() {
    const client = this.collaborators.find(
      (c) => c.role === 'CHAMPION' && c.type === 'CLIENT',
    )

    if (!client) {
      return null
    }

    return client
  }

  /**
   * Whether we are able to make corrections.
   */
  @computed
  get canMakeCorrections() {
    if (this.status === 'CLOSED') {
      return false
    }

    const currentMember = this.getCurrentMember(this.workspace.id)
    if (currentMember.hasSomePermissions('ADMIN_JOB_UPDATE')) {
      return true
    }

    return this.permissionSubject.hasSomePermissions([
      'JOB_UPDATE',
      'UPDATE_TEAM_JOBS',
    ])
  }

  /**
   * Whether we are able to make any transfer in a job.
   */
  @computed
  get canTransferJobAsAdmin() {
    return (
      !this.completed &&
      this.getCurrentMember(this.workspace.id).hasAllPermissions(
        'ADMIN_JOB_TRANSFER',
        'ADMIN_USER_LIST',
        'ADMIN_USER_READ',
        'ADMIN_JOB_VIEW',
      )
    )
  }

  @computed
  get canTransferConsultationJobAsAdmin() {
    return this.hasActiveConsultation
      ? (this.isClaimedJob || this.isOnHold) && this.canTransferJobAsAdmin
      : this.canTransferJobAsAdmin
  }

  /**
   * Whether we are able to transfer the job to client as an admin.
   */
  @computed
  get canTransferToClientAsAdmin() {
    return (
      !this.completed &&
      this.getCurrentMember(this.workspace.id).hasAllPermissions(
        'ADMIN_JOB_TRANSFER',
        'ADMIN_USER_LIST',
        'ADMIN_USER_READ',
        'ADMIN_JOB_VIEW',
      )
    )
  }

  /**
   * Whether we are able to transfer the job to pro as an admin.
   */
  @computed
  get canTransferToProAsAdmin() {
    return (
      !this.completed &&
      this.getCurrentMember(this.workspace.id).hasAllPermissions(
        'ADMIN_JOB_TRANSFER',
        'ADMIN_USER_LIST',
        'ADMIN_USER_READ',
        'ADMIN_JOB_VIEW',
        'ADMIN_JOB_CHAMPION_PROVIDER_MANAGE',
      )
    )
  }

  /**
   * Whether we are able to transfer the job to client team as an admin.
   */
  @computed
  get canTransferToClientTeamAsAdmin() {
    return (
      !this.completed &&
      this.getCurrentMember(this.workspace.id).hasAllPermissions(
        'ADMIN_ASSIGN_TEAM',
        'ADMIN_JOB_TRANSFER',
        'ADMIN_USER_LIST',
        'ADMIN_USER_READ',
        'ADMIN_JOB_VIEW',
      )
    )
  }

  /**
   * Check if the user has the admin permission to delete a job.
   */
  @computed
  get canDeleteJobAsAdmin() {
    return this.getCurrentMember(this.workspace.id).hasPermission(
      'ADMIN_JOB_DELETE',
    )
  }

  /**
   * Check if the user can delete job as a collaborator.
   */
  @computed
  get canDeleteJobAsCollaborator() {
    return (
      this.isDraft &&
      (this.isChampionClientCollaborator ||
        Boolean(this.currentTeamMemberOfOwnerTeam))
    )
  }

  /**
   * Indicates whether the user has the capability to notify pros of the availability of the job.
   */
  @computed
  get canNotifyPros() {
    return (
      this.getCurrentMember(this.workspace.id).hasPermission(
        'ADMIN_JOB_UPDATE',
      ) && this.status === 'UNCLAIMED'
    )
  }

  /**
   * Whether forfeiting is enabled.
   */
  @computed
  get canForfeit() {
    const currentUserCollaborator = this.currentUserCollaborator
    if (!currentUserCollaborator) {
      return false
    }

    return (
      this.status !== 'CLOSED' &&
      (currentUserCollaborator.type === 'PROVIDER' ||
        currentUserCollaborator.role === 'SUPPORTING') &&
      this.permissionSubject.hasAllPermissions(['JOB_FORFEIT'])
    )
  }

  @computed
  get isOnHold() {
    return this.status === 'ON_HOLD'
  }

  @computed
  get isClaimedJob() {
    return (
      this.collaborators.find(
        (member) => member.type === 'PROVIDER' && member.role === 'CHAMPION',
      ) &&
      this.status !== 'CLOSED' &&
      this.status !== 'UNDER_CONSTRUCTION' &&
      this.status !== 'ON_HOLD'
    )
  }

  @computed
  get canPutJobOnHold() {
    if (!this.isClaimedJob) {
      return false
    }
    return this.hasPermissionToHoldAndResumeJob
  }

  @computed
  get canResumeJob() {
    if (!this.isOnHold) {
      return false
    }
    return this.hasPermissionToHoldAndResumeJob
  }

  @computed
  get hasPermissionToHoldAndResumeJob() {
    const perm = 'JOB_ON_HOLD'

    const workspacePermission = this.getCurrentMember(
      this.workspace.id,
    ).hasAllPermissions(perm)

    if (this.currentUserCollaborator && workspacePermission) {
      return true
    }

    const teamPermission = this.permissionSubject.hasAllPermissions([perm])

    // if the user is part of the team and has both workspace perm and team perm
    if (workspacePermission && teamPermission) {
      return true
    }

    return this.getCurrentMember(this.workspace.id).hasAllPermissions(
      'ADMIN_JOB_ON_HOLD',
    )
  }

  /**
   * Whether we are able to add invoices manually to a job.
   */
  @computed
  get canAddInvoice() {
    return this.getCurrentMember(this.workspace.id).hasAllPermissions(
      'ADMIN_JOB_UPDATE',
    )
  }

  /**
   * Whether the user can manage billing for the job.
   */
  @computed
  get canManageBilling() {
    return this.getCurrentMember(this.workspace.id).hasAllPermissions(
      'ADMIN_BILLING_MANAGE',
    )
  }

  /**
   * Whether the user can charge a job.
   */
  @computed
  get canChargeJob() {
    return this.canManageBilling && this.isBillingEnabled && !this.isDraft
  }

  /**
   * Whether the user can refund a job.
   */
  @computed
  get canRefundJob() {
    return this.canManageBilling && this.isBillingEnabled
  }

  /**
   * Whether the user can charge/refund a job.
   */
  @computed
  get canChargeOrRefundJob() {
    return this.canChargeJob || this.canRefundJob
  }

  /**
   * Whether the user can download the binder.
   */
  @computed
  get canDownloadBinder() {
    return this.workspace.config.sureprep?.enabled && !this.isDraft
  }

  /**
   * Whether we are able to add invoices manually to a job.
   */
  @computed
  get canUpdateDeadline() {
    return (
      this.status !== 'CLOSED' &&
      this.getCurrentMember(this.workspace.id).hasAllPermissions(
        'ADMIN_JOB_UPDATE',
      )
    )
  }

  /**
   * Whether the user is able to view the Insights on the Job.
   */
  @computed
  get canViewInsights() {
    return this.getCurrentMember(this.workspace.id).hasAllPermissions(
      'INSIGHTS_VIEW',
    )
  }

  /**
   * Whether we are allowed to copy the Job.
   */
  @computed
  get canCopy() {
    // Must either be a client collaborator with permission to create jobs
    // or an admin.
    return (
      (this.permissionSubject.hasSomePermissions([
        'JOB_CREATE',
        'CREATE_TEAM_JOBS',
      ]) ||
        this.getCurrentMember(this.workspace.id).hasSomePermissions(
          'ADMIN_JOB_CREATE',
        )) &&
      !this.isDraft
    )
  }

  /**
   * Whether we are allowed to split the payout for the Job.
   */
  @computed
  get canSplitPayout() {
    return (
      // Payouts must be enabled on the workspace.
      this.workspace.features.payoutsConfig.enabled &&
      // Must have the right permission.
      this.getCurrentMember(this.workspace.id).hasSomePermissions(
        'ADMIN_SPLIT_PAYOUTS',
      ) &&
      // There should be at least one allocation.
      Boolean(this.payoutSplit?.allocations.length)
    )
  }

  /**
   * Whether we are allowed to reopen the closed Job.
   */
  @computed
  get canReopenAsAdmin() {
    // Must be a closed job with permission to reopen jobs
    return (
      this.completed &&
      this.getCurrentMember(this.workspace.id).hasPermission('ADMIN_JOB_REOPEN')
    )
  }

  /**
   * Whether the current user is allowed to view the payment distribution section.
   */
  @computed
  get canViewPaymentDistributionAsAdmin() {
    return this.getCurrentMember(this.workspace.id).hasPermission(
      'ADMIN_VIEW_PRICING',
    )
  }

  /**
   * Whether we are allowed to view the payout split.
   */
  @computed
  get canViewPayoutSplit() {
    return this.getCurrentMember(this.workspace.id).hasSomePermissions(
      'ADMIN_JOB_VIEW',
    )
  }

  /**
   * Whether the user has the permission to view the pro cut as admin
   */
  @computed
  get canViewProCut() {
    return this.getCurrentMember(this.workspace.id).hasPermission(
      'ADMIN_PRO_CUT_VIEW',
    )
  }

  /**
   * Whether the user can view the Cycles tab in the Bookkeeping tracker card.
   */
  @computed
  get canViewBookkeepingCyclesTab() {
    return this.getCurrentMember(this.workspace.id).hasPermission(
      'MANAGE_BOOKKEEPING',
    )
  }

  /**
   * Whether the user can correct bookkeeping cycles.
   */
  @computed
  get canCorrectBookkeepingCycles() {
    return this.getCurrentMember(this.workspace.id).hasPermission(
      'MANAGE_BOOKKEEPING',
    )
  }

  /**
   * Whether to show the earnings.
   */
  @computed
  get shouldShowProviderCutAmount() {
    const isProviderOrAdmin = this.isProviderCollaborator || this.canViewProCut

    return isProviderOrAdmin && this.providerCutAmount > 0
  }

  @computed
  get shouldShowJobTotal() {
    const currentMember = this.getCurrentMember(this.workspace.id)

    const isClientCollaborator = this.collaborators.some(
      (c) => c.type === 'CLIENT' && c.member.isCurrentUser,
    )

    const canViewPricing = currentMember.hasPermission('VIEW_PRICING')

    const onOwnerTeamWithViewPricing = Boolean(
      this.ownerTeam &&
        this.getTeamMember(
          this.ownerTeam.id,
          currentMember.user.publicId,
        )?.hasPermission('VIEW_PRICING'),
    )

    return (
      (isClientCollaborator && canViewPricing) || onOwnerTeamWithViewPricing
    )
  }

  /**
   * Formatted provider cut amount.
   */
  @computed
  get formattedProviderCutAmount() {
    if (!this.shouldShowProviderCutAmount) {
      return ''
    }

    // If there is a payout split defined, use the value from the current user's allocation, if any.
    // Otherwise show the full cut, as the user is likely an admin.
    const amount = this.currentUserAllocation ?? this.providerCutAmount

    return formatWithLegendPriceUnit(this.legend, amount)
  }

  /**
   * Formatted total price.
   */
  @computed
  get formattedTotal() {
    return ''
  }

  /**
   * Formatted amount paid.
   */
  @computed
  get formattedAmountPaid() {
    return ''
  }

  /**
   * Returns the provider champion, if there is one.
   */
  @computed
  get proChampion() {
    const proChampion = this.collaborators.find(
      (member) => member.type === 'PROVIDER' && member.role === 'CHAMPION',
    )
    return proChampion
  }

  /**
   * Returns the name of the client team (if there is one) on the job.
   */
  @computed
  get clientTeamName() {
    return this.ownerTeam?.name
  }

  /**
   * Returns the name of the provider team (if there is one) on the job.
   */
  @computed
  get proTeamName() {
    return this.proChampion?.team?.name
  }

  /**
   * Indicate if the job is part of the prep and review jobs
   **/
  @computed
  get containsPrepAndReviewLink() {
    return (
      this.jobType?.type === 'TAX' && this.jobType.jobLinks?.prepAndReviewLink
    )
  }

  /**
   * Returns the job tax type for this job : "PREP" | "REVIEW"
   **/
  @computed
  get prepAndReviewJobText() {
    return this.prepAndReviewJobLinkType === 'PREP' ? 'REVIEW' : 'PREP'
  }

  /**
   * Returns the id of the sibling job in the prep and review
   **/
  @computed
  get prepAndReviewJobLinkId() {
    if (this.jobType?.type !== 'TAX') {
      return null
    }
    return this.jobType.jobLinks?.prepAndReviewLink.id
  }

  /**
   * Returns the jobType of the sibling job in the prep and review
   **/
  @computed
  get prepAndReviewJobLinkType() {
    if (this.jobType?.type !== 'TAX') {
      return null
    }
    return this.jobType.jobLinks?.prepAndReviewLink.type
  }

  /**
   * Sets the modified date.
   *
   * @param date
   */
  @action.bound
  setModified(date: Date) {
    this.dateModified = date
  }

  /**
   * Whether the current user is allowed to download the job's conversation.
   */
  @computed
  get canDownloadConversation() {
    return Boolean(
      this.conversation !== null &&
        this.permissionSubject.hasAllPermissions(['CHAT_VIEW']),
    )
  }

  /**
   * Whether the current member is in the conversation.
   */
  @computed
  get isCurrentMemberInConversation() {
    const currentMemberPublicId = this.getCurrentMember(this.workspace.id).user
      .publicId
    return this.conversation?.participants.some(
      (p) => p.member.user.publicId === currentMemberPublicId,
    )
  }

  /**
   * Returns the formatted status.
   */
  @computed
  get formattedStatus() {
    switch (this.status) {
      case 'UNDER_CONSTRUCTION':
        return 'Onboarding'
      case 'INFO_GATHERING':
        return 'Info Gathering'
      case 'UNCLAIMED':
        return 'Unclaimed'
      case 'CLAIMED':
        return 'In Progress'
      case 'CLOSED':
        return 'Closed'
      case 'IDLE':
        return 'Idle'
      case 'ON_HOLD':
        return 'On Hold'
      default:
        return this.status
    }
  }

  parse(json: Partial<JobDTO>): Partial<Job> {
    const workspace = this.primeWorkspace({
      id: json.workspace_id || this.workspace.id,
    })
    const ownerTeam =
      (json.owner_team_id && this.primeTeam({ id: json.owner_team_id })) || null
    const teams = json.team_ids
      ? json.team_ids.map((id) => this.primeTeam({ id }) || Team.none)
      : null
    this.teams = teams || this.teams
    this.legend = json.legend || this.legend
    this.legendServiceType =
      (json.legend?.service_type as JobLegendServiceType) ||
      this.legendServiceType
    this.ownerTeam = ownerTeam || this.ownerTeam
    this.workspace = workspace || this.workspace
    this.allowManualPaymentCollection = json.legend
      ? Boolean(json.legend.billing_config?.allow_manual_payment_collection)
      : this.allowManualPaymentCollection

    this.collaborators.reset(json.members)
    let legendVersionId: LegendVersionId | undefined
    if (json.legend?.id) {
      legendVersionId = LegendVersionId(json.legend.id, json.legend.version)
      this.primePublishedLegend(legendVersionId)
    }
    const dateModified = maybeParseDate(json.date_modified)
    this._paidAmount = json.paid_amount ?? this._paidAmount
    this._total = json.total ?? this._total
    this.billingEnabled = json.legend
      ? !json.legend.skipBilling
      : this.billingEnabled
    this.deferPaymentInfoCollection = json.legend
      ? Boolean(json.legend.billing_config?.defer_payment_info_collection)
      : this.deferPaymentInfoCollection

    return {
      workspace,
      legendVersionId,
      requestedProId: json.requested_pro_id,
      requestedProPublicId: json.requested_pro_public_id,
      requestedPro:
        !isNil(json.requested_pro_id) && !isNil(json.requested_pro_public_id)
          ? this.primeMemberByPublicId({
              workspace_id: this.workspace.id,
              user_public_id: json.requested_pro_public_id,
            })
          : json.requested_pro_id === null
            ? null
            : undefined,
      legendId: json.legend?.id,
      legendName: json.legend_name,
      ratingConfig: json.legend?.rating_config
        ? json.legend.rating_config
        : undefined,
      allowScheduling: json.legend ? json.legend.allowScheduling : undefined,
      alwaysEnableVoiceCall: json.legend
        ? json.legend.alwaysEnableVoiceCall
        : undefined,
      allowProSelection: json.legend
        ? json.legend.allow_pro_selection
        : undefined,
      dateDeadline: maybeParseDate(json.date_deadline),
      dateCreated: maybeParseDate(json.date_created),
      // Only update dateModified if its newer than what we already have.
      dateModified:
        dateModified !== undefined && dateModified > this.dateModified
          ? dateModified
          : this.dateModified,
      resolution: !isNil(json.resolution)
        ? match(json.resolution, {
            DONE: () => Resolution.Done,
            CANCELLED: () => Resolution.Cancelled,
            TEST: () => Resolution.Test,
          })
        : json.resolution,
      dateSubmitted: maybeParseDate(json.date_transmitted),
      dateAccepted: maybeParseDate(json.date_accepted),
      dateClosed: maybeParseDate(json.date_closed),
      dateLastActivity: maybeParseDate(json.date_last_activity),
      answers: json.answers,
      paymentInfoStatus: json.payment_info_status as PaymentInfoStatus,
      id: json.id,
      shortId: json.short_id,
      stripeCharges: json.stripe_charges,
      stripeRefunds: json.stripe_refunds,
      name: json.name || json.description,
      description: json.description,
      primaryAmount: json.primary_amount,
      providerCutAmount: json.provider_cut_amount,
      couponCodes: json.coupon_codes?.join(', '),
      couponAmount: json.coupon_amount ? json.coupon_amount : undefined,
      status: json.status,
      infoLines: json.info_lines
        ? json.info_lines.map((l) => ({ title: l.title, content: l.content }))
        : undefined,
      conversation: json.layerConversation
        ? this.primeConversation({ id: json.layerConversation })
        : undefined,
      milestones:
        json.milestones &&
        json.milestones.map(
          (milestone): Milestone => ({
            id: milestone.id,
            title: milestone.title,
            completed: Boolean(milestone.completed),
            description: milestone.description || '',
            tasks: milestone.tasks.map(
              (task): Task => ({
                id: task.id,
                title: task.title,
                description: task.description || '',
                designee: task.designee,
                completed: Boolean(task.completed),
                completedAt: maybeParseDate(task.date_completed) || DateZero(),
                job: this,
                completedBy:
                  this.primeMemberByPublicId({
                    workspace_id: this.workspace.id,
                    user_public_id: task.completed_by_public_id,
                  }) || null,
              }),
            ),
          }),
        ),
      waitingOn: json.waiting_on
        ? match(json.waiting_on, {
            Provider: () => WaitingOn.Provider,
            Client: () => WaitingOn.Client,
          })
        : json.waiting_on,

      payoutSplit: PayoutSplit.parse(this.primeUser, json.payout_split),
      currentUserAllocation: json.calling_actor_allocation,
      amendments: json.amendments
        ? json.amendments.map((amendment) => ({
            creator: amendment.creator,
            customer_amount: amendment.customer_amount,
            date_created: maybeParseDate(amendment.date_created) || DateZero(),
            description: amendment.description,
            internal_note: amendment.internal_note,
            provider_amount: amendment.provider_amount,
            visibility: amendment.visibility
              ? match(amendment.visibility, {
                  PUBLIC: () => AmendmentVisibility.Public,
                  INTERNAL: () => AmendmentVisibility.Internal,
                })
              : amendment.visibility,
          }))
        : undefined,
      jobScopeItems: json.job_scope_items
        ? json.job_scope_items.map((item) => {
            return {
              id: item.id,
              name: item.name,
              description: item.description,
              quantity: item.quantity,
              courtesy_quantity: item.courtesy_quantity,
              price: item.price,
              pro_price: item.pro_price,
              inventory_item_id: item.inventory_item_id,
              inventory_item_version_id: item.inventory_item_version_id,
              bundle_id: isNotNil(item.bundle_id) ? item.bundle_id : null,
              custom_item_id: isNotNil(item.custom_item_id)
                ? item.custom_item_id
                : null,
            }
          })
        : undefined,
      billingType: json.billing_type,
      consultation: json.consultation
        ? this.consultation
          ? this.consultation.fromJSON(json.consultation)
          : Consultation.fromJSON(json.consultation, this.primeRoom)
        : undefined,
      jobType: json.type,
    }
  }
}

const lazyNone = once(
  () =>
    new Job(
      () => undefined,
      () => User.none,
      () => undefined,
      () => undefined,
      () => PublishedLegend.none,
      () => undefined,
      () => [],
      () => Member.none,
      () => Member.none,
      () => Room.none,
    ),
)

/**
 * Amendment.
 */
export interface Amendment {
  readonly creator: JobUserDTO
  readonly customer_amount: number
  readonly date_created: Date
  readonly description: string
  readonly internal_note: string
  readonly provider_amount: number
  readonly visibility: AmendmentVisibility
}

export interface JobScopeItem {
  readonly id: string
  readonly name: string
  readonly description: string
  readonly quantity: number
  readonly courtesy_quantity?: number
  readonly inventory_item_id?: string
  readonly inventory_item_version_id?: string
  readonly price?: number
  readonly pro_price?: number
  readonly bundle_id: string | null
  readonly custom_item_id: string | null
}

/**
 * Rating configuration
 */
export interface RatingConfig {
  platformRatingEnabled: boolean
  proRatingEnabled: boolean
  customerRatingMethod: RatingMethod
}

export type RatingMethod = 'thumbs' | 'stars'

/**
 * Formats a job amount.
 *
 * @param legend
 * @param value
 */
export function formatWithLegendPriceUnit(
  legend: { priceUnit?: string } | undefined,
  value: number | undefined,
): string {
  if (!isNotNil(value)) {
    return ''
  }

  const priceUnit = legend?.priceUnit
  if (!priceUnit) {
    // I know we should put this somewhere else, but I want you to
    // feel bad every time you look at this function, and it will
    // hopefully make you stand with me in my fight for getting this whole
    // thing solved in a better way.
    const moneyFormatter = new Intl.NumberFormat('en-US', {
      currency: 'usd',
      style: 'currency',
    })
    return moneyFormatter.format(value)
  }

  return `${roundToPrecision(value)} ${priceUnit}`
}

/**
 * Info Line.
 */
export interface InfoLine {
  title: string
  content: string
}

/**
 * Collaborator.
 */
export class Collaborator extends Model<JobMemberDTO, Collaborator> {
  @observable type: CollaboratorType = 'CLIENT'
  @observable role: CollaboratorRole = 'CHAMPION'
  @observable member: Member = Member.none
  @observable team: Team | null = null

  constructor(
    public job: Job,
    private primeMemberByPublicId: PrimeMemberByPublicId,
    private primeTeam: PrimeTeam,
  ) {
    super()
    makeObservable(this)
  }

  /**
   * Checks if the collaborator is what we are asking.
   *
   * @param type The type. If omitted, only checks role.
   * @param role The role. If omitted, only checks type.
   */
  is(type: CollaboratorType | null, role?: CollaboratorRole | null) {
    const matchesType = !type || type === this.type
    const matchesRole = !role || role === this.role
    return matchesType && matchesRole
  }

  parse(attrs: Partial<JobMemberDTO>): Partial<Collaborator> {
    const member =
      attrs.user &&
      this.primeMemberByPublicId({
        workspace_id: this.job.workspace.id,
        ...attrs.user,
      })
    return {
      member,
      type: attrs.type,
      role: attrs.role,
      team: attrs.team_id ? this.primeTeam({ id: attrs.team_id }) : null,
    }
  }
}

/**
 * Milestone.
 */
export interface Milestone {
  readonly id: string
  readonly title: string
  readonly description: string
  readonly completed: boolean
  readonly tasks: Task[]
}

export interface Task {
  readonly id: string
  readonly title: string
  readonly description: string
  readonly completed: boolean
  readonly designee: TaskDesignee
  readonly completedBy: Member | null
  readonly completedAt: Date
  readonly job: Job
}

/**
 * Implements the following checking algorithm:
 *
 * - If CU is a member of ANY Teams that the Job is associated with,
 *   the permissions from every team CU is a member of are aggregated
 *   and checked.
 *
 *  - If not, the Workspace Member permissions are checked.
 *
 * IMPORTANT: These do NOT replace collaborator checks!
 * Additionally, ensure that the Team Members for CU
 * have been loaded before using this.
 */
class AggregatingPermissionSubject implements PermissionSubject {
  constructor(
    private job: Job,
    private getCurrentMember: GetCurrentMember,
    private getTeamMember: GetTeamMember,
  ) {
    makeObservable(this)
  }

  hasAllPermissions(permissions: Array<Permission> | Permission): boolean {
    permissions = AggregatingPermissionSubject.normalize(permissions)
    return permissions.every((p) =>
      this.subjects.some((s) => s.hasSomePermissions(p)),
    )
  }

  hasSomePermissions(permissions: Array<Permission> | Permission): boolean {
    permissions = AggregatingPermissionSubject.normalize(permissions)
    return permissions.some((p) =>
      this.subjects.some((s) => s.hasSomePermissions(p)),
    )
  }

  @computed
  private get subjects(): PermissionSubject[] {
    const currentMember = this.getCurrentMember(this.job.workspace.id)
    const teamMembers = this.job.teams
      .map((team) => this.getTeamMember(team.id, currentMember.user.publicId))
      .filter(isNotNil)

    if (teamMembers.length === 0) {
      return [currentMember]
    }

    return teamMembers
  }

  private static normalize(perms: Permission[] | Permission) {
    return Array.isArray(perms) ? perms : [perms]
  }
}

export enum BalanceStatus {
  /**
   * We are neither owed or owe money.
   */
  BalanceZero,
  /**
   * The user owes us money.
   */
  BalancePositive,
  /**
   * The user has paid too much and should receive a refund.
   */
  BalanceNegative,
}

export enum PaymentInfoStatus {
  /**
   * Payment info has not been provided nor requested.
   */
  NotProvided = 'NOT_PROVIDED',
  /**
   * Payment info has been requested.
   */
  Requested = 'REQUESTED',
  /**
   * Payment info has been provided.
   */
  Provided = 'PROVIDED',
}

export enum WaitingOn {
  /**
   * Waiting for provider to work in the job.
   */
  Provider = 'Provider',
  /**
   * Waiting for client to work in the job.
   */
  Client = 'Client',
}

export interface PayoutSplit {
  /**
   * The kind of payout split situation we are finding ourselves in.
   */
  readonly situation: PayoutSplitSituation
  /**
   * The payout allocation list.
   */
  readonly allocations: Allocation[]
}

/**
 * Describes the payout situation.
 */
export type PayoutSplitSituation =
  | DefaultPayoutSplit
  | ManualPayoutSplit
  | AutoPayoutSplit

/**
 * Run-of-the-mill bog-standard payout, has not been messed with in any way
 * worth writing home about. Basically, it was neither manually split, nor has it
 * been auto-adjusted.
 */
export interface DefaultPayoutSplit {
  readonly type: 'Default'
}

/**
 * The payout split has been manually adjusted.
 */
export interface ManualPayoutSplit {
  readonly type: 'Manual'
  /**
   * The user that manually adjusted the split.
   */
  readonly allocatedBy: User
  /**
   * The time that the split was manually adjusted.
   */
  readonly allocatedAt: Date
}

/**
 * The split was auto-adjusted by the system.
 */
export interface AutoPayoutSplit {
  readonly type: 'Auto'
  /**
   * The time that the split was auto-adjusted.
   */
  readonly adjustedAt: Date
}

export const PayoutSplit = {
  /**
   * Empty split.
   */
  empty(): PayoutSplit {
    return { allocations: [], situation: { type: 'Default' } }
  },
  /**
   * Parses a payout split.
   */
  parse(
    primeUser: PrimeUser,
    dto: PayoutSplitDTO | null | undefined,
  ): PayoutSplit | null | undefined {
    if (!dto) {
      return dto
    }

    const allocations: Allocation[] = dto.allocations.map((allocationDto) => ({
      amount: allocationDto.amount,
      authorizedPartialPayoutAmount:
        allocationDto.authorized_partial_payout_amount,
      user: primeUser({
        public_id: allocationDto.user_public_id,
      }),
    }))

    if (dto.auto_adjusted) {
      return {
        allocations,
        situation: {
          type: 'Auto',
          adjustedAt: dto.date_auto_adjusted
            ? new Date(dto.date_auto_adjusted)
            : DateZero(),
        },
      }
    }

    if (dto.allocated_manually_by_public_id) {
      return {
        allocations,
        situation: {
          type: 'Manual',
          allocatedBy: primeUser({
            public_id: dto.allocated_manually_by_public_id,
          }),
          allocatedAt: dto.date_allocated_manually
            ? new Date(dto.date_allocated_manually)
            : DateZero(),
        },
      }
    }

    return {
      allocations,
      situation: { type: 'Default' },
    }
  },
} as const

export interface Allocation {
  /**
   * The user that the amount is allocated for.
   */
  user: User
  /**
   * The amount allocated to the user.
   */
  amount: number
  /**
   * The amount that is authorized for partial payout to the user.
   */
  authorizedPartialPayoutAmount: number
}

export enum AmendmentVisibility {
  /**
   * Amendment visibility is public.
   */
  Public = 'PUBLIC',
  /**
   * Amendment visibility is internal.
   */
  Internal = 'INTERNAL',
}
